import { isNonProductionEnvironment } from './app-environment'
import { getAppConfig } from './env.dynamic.client'

const SITE_NAME = 'Spicy Friends'
const TRAILING_SLASH_REGEX = /\/$/

export interface SeoOptions {
  title: string
  description?: string
  image?: string
  url?: string
  /** HTML `meta name="author"` content. Defaults to the site default when omitted. */
  author?: string
  /** Open Graph `og:type`. Defaults to `website`. */
  ogType?: string
  robots?: string
  keywords?: string
  /**
   * JSON-LD structured data (object or array). Serialized as `application/ld+json`.
   * Arrays are emitted as a single `{ @context, @graph }` object so Safari's
   * structured-data parser does not throw on a top-level array.
   */
  jsonLd?: unknown | unknown[]
  /** Additional scripts (e.g. application/ld+json). Merged with jsonLd script if both provided. */
  scripts?: Array<{ type: string; children: string }>
  /** Additional links (e.g. canonical). */
  links?: Array<{ rel: string; href: string; [key: string]: string }>
  /** Additional meta tags (e.g. rating). Appended to the default meta. */
  meta?: Array<{ name?: string; property?: string; content: string }>
}

export type RobotsMetaByEnvironment = {
  /** When set, used for both production and non-production unless overridden by `production` or `staging`. */
  all?: string
  production?: string
  staging?: string
}

/**
 * Robots meta `content` by deploy. `staging` applies when the app is not production
 * (Doppler staging, development, unknown, or unset).
 * Provide `all`, or both `production` and `staging`, or `all` with optional overrides.
 */
export function robotsMetaForEnvironment(config: RobotsMetaByEnvironment): string {
  const { all, production, staging } = config
  const forProduction = production ?? all
  const forStaging = staging ?? all
  if (forProduction === undefined || forStaging === undefined) {
    throw new Error(
      'robotsMetaForEnvironment: set `all`, or both `production` and `staging`, or `all` with optional overrides.'
    )
  }
  return isNonProductionEnvironment() ? forStaging : forProduction
}

function normalizeBaseUrl(baseUrl: string) {
  return baseUrl.replace(TRAILING_SLASH_REGEX, '')
}

function normalizePathname(pathname: string) {
  const ensuredLeadingSlash = pathname.startsWith('/') ? pathname : `/${pathname}`
  if (ensuredLeadingSlash === '/') return '/'
  return ensuredLeadingSlash.replace(TRAILING_SLASH_REGEX, '')
}

/**
 * Canonical URL for a browser-rendered page route, based on `APP_URL` + pathname.
 * Returns `null` for non-document endpoints where a canonical tag is not useful.
 */
export function canonicalUrlForPathname(pathname: string): string | null {
  const appConfig = getAppConfig()
  const baseUrl = appConfig.APP_URL ?? ''
  if (!baseUrl) return null

  const normalizedPathname = normalizePathname(pathname)

  // Exclude non-document endpoints (API, webhooks, health checks, static metadata endpoints).
  if (
    normalizedPathname === '/robots.txt' ||
    normalizedPathname === '/manifest.json' ||
    normalizedPathname.startsWith('/api/') ||
    normalizedPathname.startsWith('/webhooks/') ||
    normalizedPathname.startsWith('/.redwood.functions.')
  ) {
    return null
  }

  return `${normalizeBaseUrl(baseUrl)}${normalizedPathname}`
}

const SCHEMA_ORG_CONTEXT = 'https://schema.org'

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isSchemaOrgContext(value: unknown): boolean {
  if (typeof value !== 'string') return false
  const normalized = value.trim().replace(TRAILING_SLASH_REGEX, '').toLowerCase()
  return normalized === 'https://schema.org' || normalized === 'http://schema.org'
}

function graphNodeWithoutRedundantContext(node: unknown): unknown {
  if (!(isPlainObject(node) && isSchemaOrgContext(node['@context']))) {
    return node
  }
  const rest = { ...node }
  delete rest['@context']
  return rest
}

/**
 * Safari calls `root["@context"].toLowerCase()` on JSON-LD and throws
 * `TypeError` when the payload is a top-level array (arrays have no `@context`).
 * Emit a single object with `@graph` instead.
 */
function jsonLdDocument(jsonLd: unknown): unknown {
  if (!Array.isArray(jsonLd)) {
    return jsonLd
  }
  return {
    '@context': SCHEMA_ORG_CONTEXT,
    '@graph': jsonLd.map(graphNodeWithoutRedundantContext),
  }
}

export function seo(options: SeoOptions) {
  const {
    title,
    description,
    image,
    url,
    author: metaAuthor,
    ogType = 'website',
    robots,
    keywords,
    jsonLd,
    scripts = [],
    links = [],
    meta: extraMeta = [],
  } = options
  const appConfig = getAppConfig()
  const baseUrl = appConfig.APP_URL ?? ''
  const canonicalUrl = url ?? baseUrl
  const imageUrl = image
    ? image.startsWith('http')
      ? image
      : `${normalizeBaseUrl(baseUrl)}${image.startsWith('/') ? '' : '/'}${image}`
    : undefined
  const meta = [
    { title },
    ...(description ? [{ name: 'description' as const, content: description }] : []),
    ...(keywords ? [{ name: 'keywords' as const, content: keywords }] : []),
    { name: 'author', content: metaAuthor ?? 'Spicy Friends Ltd.' },
    { name: 'twitter:title', content: title },
    ...(description ? [{ name: 'twitter:description' as const, content: description }] : []),
    { name: 'twitter:creator', content: '@SpicyFriends' },
    { name: 'twitter:site', content: '@SpicyFriends' },
    { name: 'twitter:url', content: canonicalUrl },
    { name: 'facebook-domain-verification', content: 'g8e3pfo3qxfueyl97ckhmv1pq0dyoy' },
    { property: 'og:type', content: ogType },
    { property: 'og:site_name', content: SITE_NAME },
    { property: 'og:title', content: title },
    ...(description ? [{ property: 'og:description' as const, content: description }] : []),
    { property: 'og:url', content: canonicalUrl },
    { property: 'og:locale', content: 'en_US' },
    ...(imageUrl
      ? [
          { name: 'twitter:image', content: imageUrl },
          { name: 'twitter:card', content: 'summary_large_image' },
          { property: 'og:image', content: imageUrl },
        ]
      : []),
    ...(robots ? [{ name: 'robots', content: robots }] : []),
    ...extraMeta,
  ]

  const jsonLdScripts =
    jsonLd !== undefined ? [{ type: 'application/ld+json', children: JSON.stringify(jsonLdDocument(jsonLd)) }] : []

  return {
    meta,
    ...(jsonLdScripts.length > 0 || scripts.length > 0 ? { scripts: [...jsonLdScripts, ...scripts] } : {}),
    ...(links.length > 0 ? { links } : {}),
  }
}
